import { isRussian } from "../../locales/display";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Box } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Panel from "@ui/panel/Panel";
import Signal from "badmfck-signal";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { GD } from "src/GD";
import { IPaymentSession } from "src/types/types";
import { getNotificationErrorMessage, notify } from "../../tools/notify";
import { TFraudGroupCheckResult, TFraudItemResult, TFraudRule, TFraudRuleResult } from "./types";

export const S_TEST_RULES_POPUP = new Signal<Record<string, number | string> | null>("S_TEST_RULES_POPUP");

const PER_PAGE = 40;
// Trigger loading the next page when the user scrolls within this many px of the bottom.
const SCROLL_LOAD_THRESHOLD = 48;

// Pull the session array out of whatever envelope the API returns.
const normalizeSessionItems = (data: any): IPaymentSession[] => {
	if (Array.isArray(data?.items)) return data.items;
	if (Array.isArray(data?.rows)) return data.rows;
	if (Array.isArray(data)) return data;
	return [];
};

// Resolve the total page count from the various pagination shapes the API may use.
const resolveTotalPages = (data: any): number | undefined => {
	if (data?.pagination?.totalPages != null) return Number(data.pagination.totalPages);
	if (data?.pages?.totalPages != null) return Number(data.pages.totalPages);
	const total = data?.pagination?.total ?? data?.pages?.total ?? data?.total;
	const perPage = data?.pagination?.perPage ?? data?.pages?.perPage ?? PER_PAGE;
	if (total != null && perPage) return Math.ceil(Number(total) / Number(perPage));
	return undefined;
};

// Slate palette shared with the results styling below, so the whole popup reads as one piece.
const UI = {
	border: "#e2e8f0",
	borderStrong: "#cbd5e1",
	text: "#334155",
	muted: "#64748b",
	faint: "#94a3b8",
	accent: "#3b82f6",
	accentSoft: "#eff6ff",
	accentBorder: "#bfdbfe",
	dark: "#1e293b",
};

const SUCCESS_STATUSES = new Set(["success", "succeeded", "completed", "approved", "captured", "settled", "paid"]);
const FAIL_STATUSES = new Set(["declined", "failed", "fail", "error", "rejected", "cancelled", "canceled", "expired"]);

const statusColor = (status?: string): string => {
	const v = (status ?? "").toLowerCase();
	if (SUCCESS_STATUSES.has(v)) return "#16a34a";
	if (FAIL_STATUSES.has(v)) return "#dc2626";
	return UI.muted;
};

const formatAmount = (session: IPaymentSession): string => {
	const amount = typeof session.amount === "number" ? (session.amount / 100).toFixed(2) : session.amount;
	return `${amount} ${session.currency ?? ""}`.trim();
};

const badge = (text: string, bg: string): JSX.Element => (
	<span style={{ fontSize: "11px", fontWeight: 600, color: "#fff", background: bg, borderRadius: "4px", padding: "1px 6px" }}>{text}</span>
);

// Soft, low-contrast badge (tinted background + colored text) used for transaction statuses.
const softBadge = (text: string, color: string): JSX.Element => (
	<span
		style={{
			fontSize: "10px",
			fontWeight: 700,
			letterSpacing: "0.02em",
			textTransform: "uppercase",
			color,
			background: `${color}14`,
			borderRadius: "4px",
			padding: "2px 6px",
			whiteSpace: "nowrap",
		}}>
		{text}
	</span>
);

const CheckMark: FC = () => { useLocale(); return ((
	<svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M2.5 6.5L5 9L9.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
)); };

const labelStyle: React.CSSProperties = {
	fontSize: "12px",
	fontWeight: 700,
	color: UI.text,
	textTransform: "uppercase",
	letterSpacing: "0.04em",
	display: "flex",
	alignItems: "center",
	gap: "8px",
};

const counterBadge: React.CSSProperties = {
	fontSize: "11px",
	fontWeight: 600,
	color: UI.accent,
	background: UI.accentSoft,
	border: `1px solid ${UI.accentBorder}`,
	borderRadius: "999px",
	padding: "1px 8px",
	textTransform: "none",
	letterSpacing: 0,
};

const linkStyle: React.CSSProperties = { fontSize: "12px", fontWeight: 600, color: UI.muted, cursor: "pointer" };

// Human-readable operator symbols shown in the results instead of raw codes (gt, eq, ...).
const OPERATOR_SYMBOLS: Record<string, string> = {
	eq: "=",
	neq: "≠",
	gt: ">",
	lt: "<",
	gte: "≥",
	lte: "≤",
	same: "same",
	get not_same() { return msg("notSame_f391fa18"); },
	each: "each",
};

const formatVal = (v: unknown): string => (v === undefined || v === null || v === "" ? "—" : String(v));

const renderItem = (item: TFraudItemResult, key: number): JSX.Element => {
	// non-evaluable > matched > no-match
	const color = !item.evaluable ? "#f59e0b" : item.matched ? "#22c55e" : "#475569";
	const mark = !item.evaluable ? "skipped" : item.matched ? "match" : msg("noMatch");
	const symbol = OPERATOR_SYMBOLS[item.operator] ?? item.operator;
	// For evaluable items build a clean line from structured fields; the mark badge already conveys the outcome.
	// For skipped items fall back to the backend explanation (field not mapped / operator not supported).
	const text = item.evaluable ? `${item.field}: ${formatVal(item.actual)} ${symbol} ${formatVal(item.expected)}` : item.reason;
	return (
		<div key={key} style={{ display: "flex", gap: "8px", alignItems: "baseline", fontSize: "12px", padding: "2px 0" }}>
			<span style={{ color, fontWeight: 600, minWidth: "64px" }}>{mark}</span>
			<span style={{ color: "#334155" }}>{text}</span>
		</div>
	);
};

const PERIOD_SHORT: Record<string, string> = { minute: "m", hour: "h", day: "d", week: "w", month: "mo" };

const renderThreshold = (t: TFraudRuleResult["threshold"]): JSX.Element | null => {
	if (!t.enabled) return null;
	const symbol = OPERATOR_SYMBOLS[t.operator] ?? t.operator;
	const per = `${t.timeframe}${PERIOD_SHORT[t.period] ?? t.period}`;
	const metric =
		t.units === "%"
			? `${(t.percentage ?? 0).toFixed(1)}% (${t.count}/${t.base_count ?? 0})${t.percent_base && t.percent_base !== "all" ? msg("templateBase_5a9dff6e", { v0: t.percent_base }) : ""}`
			: msg("templateItems_78b8b55e", { v0: t.count });
	const target = `${t.value}${t.units === "%" ? "%" : ""}`;
	return (
		<div style={{ display: "flex", gap: "8px", alignItems: "baseline", fontSize: "12px", padding: "2px 0" }}>
			<span style={{ color: t.passed ? "#22c55e" : "#475569", fontWeight: 600, minWidth: "64px" }}>{t.passed ? msg("passed") : msg("notPassed")}</span>
			<span style={{ color: "#334155" }}>{msg("templateThresholdPer_dbe812f6", { v0: metric, v1: symbol, v2: target, v3: per })}</span>
		</div>
	);
};

const renderRule = (rule: TFraudRuleResult, key: number): JSX.Element => {
	const disabled = rule.status === "disabled";
	return (
		<div key={key} style={{ border: "1px solid #334155", borderRadius: "6px", padding: "8px", marginTop: "6px", opacity: disabled ? 0.6 : 1 }}>
			<div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
				{disabled ? badge(msg("disabled_0c7ef33e"), "#475569") : badge(rule.fired ? msg("fired") : msg("notFired"), rule.fired ? "#dc2626" : "#475569")}
				<span style={{ fontWeight: 600 }}>{rule.rule_name}</span>
				<span style={{ fontSize: "11px", color: "#475569" }}>#{rule.rule_id}</span>
				{badge(rule.action, "#334155")}
				<span style={{ fontSize: "11px", color: "#475569" }}>{rule.combinator.toUpperCase()}</span>
				{!disabled && rule.threshold.enabled && badge("threshold", "#334155")}
			</div>
			<div style={{ fontSize: "12px", color: "#475569", marginTop: "4px" }}>{rule.reason}</div>
			{!disabled && <div style={{ marginTop: "4px" }}>{rule.items.map(renderItem)}</div>}
			{!disabled && renderThreshold(rule.threshold)}
		</div>
	);
};

const renderResult = (result: TFraudGroupCheckResult, key: number): JSX.Element => (
	<div key={key} style={{ borderTop: "1px solid #1e293b", paddingTop: "8px", marginTop: "8px" }}>
		<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
			<span style={{ fontWeight: 600 }}>{msg("transaction_d0e58d26")}{result.trx_id}</span>
			{badge(msg("templateFired_2650fc76", { v0: result.fired_rules.length, v1: result.rules.length }), result.fired_rules.length ? "#dc2626" : "#475569")}
		</div>
		{result.rules.map(renderRule)}
	</div>
);

export const TestRulesPopup: FC = () => {
	const localeRevision = useLocale();
	const requestData = S_TEST_RULES_POPUP.use();
	const [_rule, setRule] = useState<TFraudRule | null>(null);
	const [_ruleMetaData, _setRuleMetaData] = useState<{ id: number } | null>(null);
	const [groups] = GD.REQ_FRAUD_GROUPS_GET.useValue(undefined, undefined, [requestData]);

	const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
	const [items, setItems] = useState<IPaymentSession[]>([]);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [initialLoading, setInitialLoading] = useState(false);
	const [pageLoading, setPageLoading] = useState(false);
	const [testLoading, setTestLoading] = useState(false);
	const [results, setResults] = useState<TFraudGroupCheckResult[] | null>(null);

	// Refs mirror pagination state so the scroll handler always reads fresh values
	// without being re-created (and re-attached) on every page load.
	const pageRef = useRef(0);
	const hasMoreRef = useRef(false);
	const loadingRef = useRef(false);

	useEffect(() => {
		if (!requestData) {
			setRule(null);
			return;
		}
		// GD.REQ_FRAUD_RULE_GET.request({ ruleID: requestData.ruleID }).then(res => {
		// 	setRule(res ? (res.data as TFraudRule) : null);
		// 	setRuleMetaData(res ? { id: res.id } : null);
		// });
	}, [requestData, setRule]);

	const loadPage = useCallback(
		async (page: number) => {
			if (loadingRef.current) return;
			loadingRef.current = true;
			if (page === 0) setInitialLoading(true);
			else setPageLoading(true);

			try {
				const res = await GD.REQ_API.request({
					method: "session/list",
					data: {
						...requestData,
						pagination: { page, perPage: PER_PAGE },
					},
				});

				if (res?.error) {
					hasMoreRef.current = false;
					return;
				}

				const newItems = normalizeSessionItems(res?.data);
				const totalPages = resolveTotalPages(res?.data);
				pageRef.current = page;
				hasMoreRef.current = totalPages != null ? page + 1 < totalPages : newItems.length === PER_PAGE;

				setItems(prev => (page === 0 ? newItems : [...prev, ...newItems]));
			} finally {
				loadingRef.current = false;
				setInitialLoading(false);
				setPageLoading(false);
			}
		},
		[requestData],
	);

	useEffect(() => {
		if (!requestData) {
			setItems([]);
			setSelectedIds([]);
			setResults(null);
			pageRef.current = 0;
			hasMoreRef.current = false;
			return;
		}
		// Reset and load the first page whenever the popup is (re)opened.
		setItems([]);
		setSelectedIds([]);
		setResults(null);
		pageRef.current = 0;
		hasMoreRef.current = false;
		loadPage(0);
	}, [requestData, loadPage]);

	const onScroll = useCallback(
		(e: React.UIEvent<HTMLDivElement>) => {
			if (!hasMoreRef.current || loadingRef.current) return;
			const el = e.currentTarget;
			if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_LOAD_THRESHOLD) {
				loadPage(pageRef.current + 1);
			}
		},
		[loadPage],
	);

	const toggleId = useCallback((id: number) => {
		setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
	}, []);

	const selectAllVisible = useCallback(() => {
		setSelectedIds(items.map(i => i.id));
	}, [items]);

	const clearSelection = useCallback(() => {
		setSelectedIds([]);
	}, []);

	const onTest = useCallback(async () => {
		if (testLoading) return;
		setTestLoading(true);
		setResults(null);
		try {
			const res = await GD.REQ_API.request({
				method: "fraud/test",
				httpMethod: "POST",
				data: {
					group_id: selectedGroupId ?? 0,
					trx_ids: selectedIds.length ? selectedIds : [1],
				},
			});

			if (res?.error) {
				notify.error(getNotificationErrorMessage(res.error, msg("unableToTestFraudRules")));
				return;
			}

			setResults((res?.data as TFraudGroupCheckResult[]) ?? []);
		} catch (error) {
			notify.error(getNotificationErrorMessage(error, msg("unableToTestFraudRules")));
		} finally {
			setTestLoading(false);
		}
	}, [localeRevision, testLoading, selectedGroupId, selectedIds]);

	const closePopup = useCallback(() => {
		S_TEST_RULES_POPUP.invoke(null);
	}, []);

	if (!requestData) return null;

	return (
		<Panel
			sx={{
				minWidth: "800px",
				maxWidth: "900px",
				height: "85vh",
			}}
			decorator="absolute"
			label={msg("testFraudRules")}
			onClose={closePopup}>
			<Box sx={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column", gap: "12px" }}>
				{/* Group picker: a row of pills instead of a native dropdown. */}
				<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
					<div style={labelStyle}>{msg("group")}</div>
					<div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
						{(groups ?? []).length === 0 && <span style={{ fontSize: "12px", color: UI.faint }}>{msg("noGroupsAvailable")}</span>}
						{(groups ?? []).map(group => {
							const active = selectedGroupId === group.id;
							return (
								<Box
									key={group.id}
									onClick={() => setSelectedGroupId(active ? null : group.id)}
									sx={{
										fontSize: "12px",
										fontWeight: 600,
										padding: "5px 12px",
										borderRadius: "999px",
										cursor: "pointer",
										color: active ? "#fff" : UI.text,
										background: active ? UI.dark : "#fff",
										border: `1px solid ${active ? UI.dark : UI.borderStrong}`,
										transition: "background .15s, border .15s, color .15s",
										"&:hover": { borderColor: active ? UI.dark : UI.accent },
									}}>
									{group.name}
								</Box>
							);
						})}
					</div>
				</div>

				{/* Transaction picker: a scrollable list of selectable cards with infinite scroll. */}
				<div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: "6px" }}>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<div style={labelStyle}>{msg("transactions")}{selectedIds.length > 0 && <span style={counterBadge}>{selectedIds.length}{msg("selected")}</span>}
						</div>
						<div style={{ display: "flex", gap: "12px" }}>
							<span
								style={{ ...linkStyle, color: items.length ? UI.muted : UI.faint, pointerEvents: items.length ? "auto" : "none" }}
								onClick={selectAllVisible}>{msg("selectAll")}</span>
							<span
								style={{ ...linkStyle, color: selectedIds.length ? UI.muted : UI.faint, pointerEvents: selectedIds.length ? "auto" : "none" }}
								onClick={clearSelection}>{msg("clear")}</span>
						</div>
					</div>
					<div
						onScroll={onScroll}
						style={{
							flex: 1,
							minHeight: "200px",
							overflow: "auto",
							border: `1px solid ${UI.border}`,
							borderRadius: "8px",
							background: "#fff",
							padding: "4px",
							display: "flex",
							flexDirection: "column",
							gap: "2px",
						}}>
						{initialLoading ? (
							<div style={{ padding: "20px", textAlign: "center", fontSize: "12px", color: UI.muted }}>{msg("loadingTransactions_d69971d3")}</div>
						) : items.length === 0 ? (
							<div style={{ padding: "20px", textAlign: "center", fontSize: "12px", color: UI.faint }}>{msg("noTransactionsFound")}</div>
						) : (
							items.map(session => {
								const selected = selectedIds.includes(session.id);
								return (
									<Box
										key={session.id}
										onClick={() => toggleId(session.id)}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: "10px",
											padding: "7px 8px",
											borderRadius: "6px",
											cursor: "pointer",
											background: selected ? UI.accentSoft : "transparent",
											border: `1px solid ${selected ? UI.accentBorder : "transparent"}`,
											transition: "background .12s, border .12s",
											"&:hover": { background: selected ? UI.accentSoft : "#f8fafc" },
										}}>
										<span
											style={{
												width: "16px",
												height: "16px",
												flexShrink: 0,
												borderRadius: "4px",
												border: `1px solid ${selected ? UI.accent : UI.borderStrong}`,
												background: selected ? UI.accent : "#fff",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}>
											{selected && <CheckMark />}
										</span>
										<span style={{ fontSize: "12px", fontWeight: 700, color: UI.text, minWidth: "52px" }}>#{session.id}</span>
										<span style={{ fontSize: "12px", fontWeight: 600, color: UI.text, whiteSpace: "nowrap" }}>{formatAmount(session)}</span>
										{session.status && softBadge(session.status, statusColor(session.status))}
										<span
											style={{
												fontSize: "12px",
												color: UI.muted,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												flex: 1,
											}}>
											{session.email}
										</span>
									</Box>
								);
							})
						)}
						{pageLoading && <div style={{ padding: "8px", textAlign: "center", fontSize: "11px", color: UI.faint }}>{msg("loadingMore")}</div>}
					</div>
				</div>

				<Button onClick={onTest} disabled={testLoading /* || selectedGroupId === null || selectedIds.length === 0 */}>
					{testLoading ? msg("testing") : selectedIds.length ? msg("templateTestTransaction_bd2ec791", { v0: selectedIds.length, v1: !isRussian() && selectedIds.length > 1 ? "s" : "" }) : msg("test")}
				</Button>

				{results !== null && (
					<Box sx={{ overflow: "auto", flexShrink: 0, maxHeight: "40%" }}>
						<label>{msg("results")}</label>
						{results.length === 0 ? (
							<div style={{ fontSize: "12px", color: "#475569", marginTop: "4px" }}>{msg("noResultsNoMatchingTransactionsFound")}</div>
						) : (
							results.map(renderResult)
						)}
					</Box>
				)}
			</Box>
		</Panel>
	);
};
